/**
 * The player's decks for the deck picker (docs/design/07 §4; owner decision Q16: own decks only):
 * `play.my_decks()` for the list, `play.load_deck(id)` + play's own validator (`@play/catalog`
 * `normaliseDeck` / `validateDeck`, live ban list from `play.live_bans()`) for eligibility per tier
 * (owner decision Q21): tier 1 accepts any legal deck, tier 2 too with unimplemented cards marked
 * "manual", tier 3 needs every card implemented (hidden while tier 3 is disabled).
 */
import { type DeckIssue, RuneboundDeckSchema, normaliseDeck, validateDeck } from '@play/catalog';
import type { DeckList } from '@play/protocol';
import { useQueries, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ensureCatalog } from '../../lib/catalog';
import { loadLocal } from '../../lib/local';
import { playDb } from '../../lib/supabase';

/** An untyped PostgREST RPC answer (parsed with zod right after). */
type RpcResult = { data: unknown; error: { message: string } | null };

/** A `play.my_decks()` row (runebound-app migration 0041). */
export const MyDeckRowSchema = z.object({
  id: z.string(),
  name: z.string().catch('Untitled deck'),
  format: z.string().nullable().catch(null),
  legend_printing_id: z.string().nullable().catch(null),
  legend_card_id: z.string().nullable().catch(null),
  legend_image_url: z.string().nullable().catch(null),
  champion_card_id: z.string().nullable().catch(null),
  updated_at: z.string().nullable().catch(null),
  main_count: z.number().catch(0),
  rune_count: z.number().catch(0),
  battlefield_count: z.number().catch(0),
  side_count: z.number().catch(0),
});
export type MyDeck = z.infer<typeof MyDeckRowSchema>;

/** The caller's decks, newest first. */
export function useMyDecks(enabled: boolean) {
  return useQuery({
    queryKey: ['play', 'my_decks'],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<MyDeck[]> => {
      const res: RpcResult = await playDb().rpc('my_decks');
      if (res.error) throw new Error(res.error.message);
      return z.array(MyDeckRowSchema).parse(res.data ?? []);
    },
  });
}

/** Live banned names (falls back to the static list inside `validateDeck` when empty). */
async function liveBans(): Promise<string[]> {
  const res: RpcResult = await playDb().rpc('live_bans');
  if (res.error) return [];
  const rows = z.array(z.object({ name: z.string() })).safeParse(res.data ?? []);
  return rows.success ? [...new Set(rows.data.map((r) => r.name))] : [];
}

/** Eligibility of one deck per tier. */
export interface DeckCheck {
  legal: boolean;
  issues: DeckIssue[];
  /** Unimplemented (manual in tier 2) card names, with copies. */
  manual: { cardId: string; name: string }[];
  /** The normalised deck (art preloading when a match starts). */
  deck: DeckList;
  /** Cards implemented / total distinct cards. */
  implemented: number;
  distinct: number;
  tiers: { 1: boolean; 2: boolean; 3: boolean };
}

/** Loads and checks one deck. */
export async function checkDeck(deckId: string): Promise<DeckCheck> {
  const [catalog, bans, local] = await Promise.all([ensureCatalog(), liveBans(), loadLocal()]);
  const res: RpcResult = await playDb().rpc('load_deck', { p_deck_id: deckId });
  if (res.error) throw new Error(res.error.message);
  if (res.data === null) throw new Error('Deck not found.');
  const raw = RuneboundDeckSchema.parse(res.data);
  const normalised = normaliseDeck(raw, catalog);
  const validation = validateDeck(normalised.deck, catalog, { bannedNames: bans });
  const issues = [...normalised.issues, ...validation.issues];
  const coverage = local.coverageFor(catalog);
  const ids = new Set<string>([
    normalised.deck.legend.cardId,
    ...(normalised.deck.champion ? [normalised.deck.champion.cardId] : []),
    ...normalised.deck.main.map((e) => e.cardId),
  ]);
  const manual = [...ids]
    .filter((id) => coverage.status(id) !== 'implemented')
    .map((id) => ({ cardId: id, name: catalog.card(id)?.name ?? id }))
    .sort((a, b) => (a.name < b.name ? -1 : 1));
  const legal = issues.length === 0;
  return {
    legal,
    issues,
    manual,
    deck: normalised.deck,
    implemented: ids.size - manual.length,
    distinct: ids.size,
    tiers: { 1: legal, 2: legal, 3: legal && manual.length === 0 },
  };
}

/** `checkDeck` through the query cache. */
export function useDeckCheck(deckId: string | null) {
  return useQuery({
    queryKey: ['play', 'deck_check', deckId],
    enabled: deckId !== null,
    staleTime: 60_000,
    queryFn: () => checkDeck(deckId ?? ''),
  });
}

/** Whether a deck is playable in a tier: `true` / `false`, or `undefined` while its check runs (or failed). */
export type DeckLegality = (deckId: string) => boolean | undefined;

/**
 * Legality of every listed deck in `tier`, from the same cached checks the deck tiles run (no extra
 * requests: the tiles already ask for each deck).
 */
export function useDecksLegality(list: readonly { id: string }[] | undefined, tier: 1 | 2 | 3): DeckLegality {
  const ids = (list ?? []).map((d) => d.id);
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['play', 'deck_check', id],
      staleTime: 60_000,
      queryFn: () => checkDeck(id),
    })),
  });
  const known = new Map<string, boolean>();
  results.forEach((r, i) => {
    const id = ids[i];
    if (id !== undefined && r.data) known.set(id, r.data.tiers[tier]);
  });
  return (id) => known.get(id);
}

const LAST_DECK_KEY = 'runebound.play.lastDeck';

/** The deck last picked on this device (`/play` and rooms share it), or `null`. */
export function readLastDeck(): string | null {
  try {
    return localStorage.getItem(LAST_DECK_KEY);
  } catch {
    return null;
  }
}

/** Remembers the picked deck on this device (best effort). */
export function rememberLastDeck(deckId: string): void {
  try {
    localStorage.setItem(LAST_DECK_KEY, deckId);
  } catch {
    // Not persisted: fine.
  }
}

/**
 * The deck to keep selected (QA 2026-10-08):
 *
 * - a deck the player picked (`explicit`: a click, or `?deck=` from runebound.gg) stays selected while
 *   it is in the list, legal or not (the panel says why it can't be played);
 * - otherwise (the device's last deck, or nothing yet) the selection is automatic: the current deck while
 *   it is not known to be illegal, else the newest deck known to be legal for the tier (`my_decks` is
 *   newest first), else the newest deck, so a player with decks always has one picked (the room page
 *   started with none selected and "Join with this deck" stayed disabled with no hint why) and is not
 *   handed an illegal one while a legal one exists (/play preselected the newest deck even when illegal).
 */
export function keepDeckSelection(
  list: readonly { id: string }[] | undefined,
  current: string | null,
  options: { explicit?: boolean; legal?: DeckLegality } = {},
): string | null {
  if (!list || list.length === 0) return current;
  const listed = current !== null && list.some((d) => d.id === current);
  const legal = options.legal ?? (() => undefined);
  if (listed && (options.explicit === true || legal(current) !== false)) return current;
  const firstLegal = list.find((d) => legal(d.id) === true);
  if (firstLegal) return firstLegal.id;
  // Nothing known to be legal (yet): keep a listed deck, else the newest.
  if (listed) return current;
  return list.find((d) => legal(d.id) !== false)?.id ?? list[0]?.id ?? null;
}
